import { useEffect, useState } from 'react';
import styles from './ConflictResolution.module.scss';
import { getHistoryRows, HistoryRow, HistoryRowDetail } from '@/library/ClubHistoryHelper';
import { AppSettingsDB, OfflineQueueDB } from '@/db/repositories';
import { useNavigate } from 'react-router-dom';
import { postSaveTourBagMstrEntity } from '@/api/endpoints';
import { OfflineQueueModel } from '@/db/models';
import { TourBagHistoryModel } from '@/api/models';
import dayjs from 'dayjs';
import OfflineModal from '@/common/OfflineModal';

class Conflict {
  id = 0;
  modelName = '';
  originalDate = new Date();
  originalModifyBy = '';
  currentDate = new Date();
  diffs = [] as HistoryRowDetail[];
}
interface IProps {
  offlineQueue: OfflineQueueModel[];
}

export default function ConflictResolution({ offlineQueue }: IProps) {
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [showOfflineModal, setShowOfflineModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const getHistory = async () => {
      let rowGuid = '';
      let currentModifyDate = new Date();
      let modelName = '';
      //get the payload based on headtype
      let thisConflicts = offlineQueue.map((q) => {
        let historyRecords = [] as TourBagHistoryModel[];
        const headTypeId = q.payload.headTypeId ? q.payload.headTypeId : 0;
        switch (headTypeId) {
          case null:
          case 0: //ball shoe glove
            rowGuid = q.payload.tourBagBsg[0].rowGuid;
            currentModifyDate = q.payload.tourBagBsg[0].modifyDate;
            historyRecords = [q.payload.tourBagBsg[0], q.error?.payload];
            modelName = 'Ball, Shoe, Glove';
            break;
          case 1: //driver, fairway, wedge, hybrid, putter
          case 2:
          case 3:
          case 5:
          case 6: {
            //find the matching club in the source data for the current club
            rowGuid = q.payload.tourBagDetail[0].rowGuid;
            currentModifyDate = q.payload.tourBagDetail[0].modifyDate;
            modelName = q.error?.payload.headModelName;
            //Wedge name uses familyName
            if (headTypeId == 5 && q.error?.payload.familyName) {
              modelName += ` (${q.error.payload.familyName})`;
            }
            historyRecords = [q.payload.tourBagDetail[0], q.error?.payload];
            break;
          }
          case 4: //iron, utility
          case 7: {
            //sets are different than individual clubs. Check accordingly
            if (q.payload.tourBagIronDetail.length > 0) {
              //find the matching club in the source data for the current club
              rowGuid = q.payload.tourBagIronDetail[0].rowGuid;
              currentModifyDate = q.payload.tourBagIronDetail[0].modifyDate;
              modelName = q.payload.tourBagIronDetail[0].headModelName.startsWith(q.payload.tourBagIronDetail[0].familyName)
                ? q.payload.tourBagIronDetail[0].headModelName
                : `${q.payload.tourBagIronDetail[0].familyName} ${q.payload.tourBagIronDetail[0].headModelName}`;
              historyRecords = [q.payload.tourBagIronDetail[0], q.error?.payload];
            } else {
              //set
              currentModifyDate = q.payload.tourBagIronSets[0].modifyDate;
              modelName = (headTypeId === 4 ? 'Iron Set Name: ' : 'Utility Set Name: ') + q.payload.tourBagIronSets[0].tourBagIronSetName;
              historyRecords = [];
            }
            break;
          }
        }
        let historyRows = new Array<HistoryRow>();
        let diffs = new Array<HistoryRowDetail>();
        if (historyRecords.length > 0) {
          historyRows = getHistoryRows(rowGuid, headTypeId, historyRecords, false);
          diffs = historyRows[0].diffs;
        }

        return {
          id: q.id,
          modelName: modelName,
          originalDate: new Date(q.error?.payload.modifyDate),
          originalModifyBy: q.error?.payload.modifyUserName || '',
          currentDate: currentModifyDate,
          diffs: diffs,
        } as Conflict;
      });
      setConflicts(thisConflicts);
    };
    getHistory();
  }, []);

  const overrideChanges = async (id: number) => {
    const lastSyncDateEntry = await AppSettingsDB.get('lastSyncDate');
    const lastSyncDate = lastSyncDateEntry?.value?.toString() || new Date().toString();
    const conflict = offlineQueue.find((q) => q.id == id);
    if (conflict) {
      await postSaveTourBagMstrEntity({ tourBagMstrData: conflict.payload, lastSyncDate: lastSyncDate, forceUpdate: true });
      await deleteRecordFromQueue(id);
    }
  };

  const deleteRecordFromQueue = async (id: number) => {
    await OfflineQueueDB.delete(id);
    const thisConflicts = [...conflicts.filter((c) => c.id !== id)];
    //if no more conflist left in the queue, redirect to dashboard to refresh all data
    if (thisConflicts.length == 0) {
      setShowOfflineModal(true);
    } else {
      setConflicts(thisConflicts);
    }
  };

  const handleCloseOfflineModal = () => {
    navigate('/');
  };

  return (
    <>
      <div className={styles.modal}>
        <header className={styles.navbar}>
          <header />
          <h1 className={styles.pageTitle}>
            <span>Tour Bag Sync</span>
          </h1>
        </header>
        <div className={styles.background}>
          <div className={styles.intro}>
            Some of your offline changes conflict with updates that were saved while you were offline.
            <ul>
              <li>
                <span className={styles.green}>GREEN</span> = Your Changes
              </li>
              <li>
                <span className={styles.red}>RED</span> = Current Specs
              </li>
            </ul>
            <p>Please review the conflicts and select the option that reflects which version you want to keep.</p>
          </div>
          {conflicts.map((conflict, ix) => (
            <div className={styles.card} key={ix}>
              {conflict.diffs.length > 0 && (
                <>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>{conflict.modelName}</div>
                  </div>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>Your changes: {dayjs(conflict.currentDate).format('MM-DD-YYYY')}</div>
                  </div>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>
                      Previous changes made by {conflict.originalModifyBy} on {dayjs(conflict.originalDate).format('MM-DD-YYYY')}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div className={styles.cardContainer}>
                      {conflict.diffs?.map((diff, ix) => (
                        <div key={ix} className={styles.cardField}>
                          <div className={styles.fieldName}>{diff.fieldName}</div>
                          <div className={styles.cardHeader}>
                            {diff.previous !== null && <div className={`${styles.diff} ${styles.previous}`}>{diff.previous}</div>}
                            <div className={`${styles.diff} ${styles.current}`}>{diff.current}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <hr />
                  <div className={styles.buttons}>
                    <button className={styles.saveButton} onClick={() => overrideChanges(conflict.id)}>
                      Replace with My Changes
                    </button>
                    <button className={styles.cancelButton} onClick={() => deleteRecordFromQueue(conflict.id)}>
                      Keep Current Specs
                    </button>
                  </div>
                </>
              )}
              {conflict.diffs.length == 0 && (
                <>
                  <div className={styles.ironSetMessage}>You made a change to a set and this will not overwrite the previous changes that were made.</div>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>{conflict.modelName}</div>
                  </div>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>Your changes: {dayjs(conflict.currentDate).format('MM-DD-YYYY')}</div>
                  </div>
                  <div className={styles.cardHeader}>
                    <div className={styles.date}>Previous changes made on {dayjs(conflict.originalDate).format('MM-DD-YYYY')}</div>
                  </div>
                  <hr />
                  <div className={styles.buttons}>
                    <button className={styles.cancelButton} onClick={() => deleteRecordFromQueue(conflict.id)}>
                      Accept
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <OfflineModal onCancel={() => {}} onClose={handleCloseOfflineModal} isOpen={showOfflineModal} modalType={'Synced'} />
    </>
  );
}
